import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icon } from '@pwc/pwc-deals-ui';
import {
  CommentTarget,
  NavigationNodeId,
  USER_ROLES,
} from '@pwc/pwc-deals-web-common';
import get from 'lodash/get';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import FieldCommentWrapper from 'common/components/FieldCommentWrapper';
import NarrativeSection from '../NarrativeSection/NarrativeSection';
import { viewHypothesisPath } from '../../../common/services/urlService';
import { analysisLinkBuilder } from '../../analyses/common/analysisUrlHelper';
import { getProject } from '../../project/projectSlice';

import './ConnectedContent.scss';
import { useKFATitleI18n } from '../../../common/hooks/useKFATitleI18n';

const ConnectedContent = ({
  projectId,
  classModifier,
  viewAsClient,
  sortByDateModified = true,
  linkedConnections = null,
}) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const [connectedContent, setConnectedContent] = useState(null);
  const [error] = useState();
  const { navigation = [] } = useSelector(getProject);
  const kfaNavigationData = useMemo(
    () => navigation.find((nav) => nav.id === NavigationNodeId.KEY_FOCUS_AREAS),
    [navigation]
  );
  const { url } = useRouteMatch();
  const analysisId = get(linkedConnections, 'connectedAnalyses.[0].contentId');
  const hypothesesId = get(linkedConnections, 'connectedKfas.[0].contentId');
  const { t } = useTranslation('');

  const contentTypes = useMemo(() => {
    if (url.includes('key-focus-areas')) {
      return ['analyses'];
    }
    return [...(kfaNavigationData?.isHidden ? [] : ['hypotheses']), 'analyses'];
  }, [url, kfaNavigationData]);

  useEffect(() => {
    if (linkedConnections) {
      setConnectedContent({
        analyses: linkedConnections.connectedAnalyses,
        hypotheses: linkedConnections.connectedKfas,
      });
    }
    return () => setConnectedContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, hypothesesId]);

  const analysesPath = useMemo(() => {
    return get(connectedContent, 'analyses', []).length > 0
      ? analysisLinkBuilder(connectedContent.analyses, projectId, isPwcUser)
      : null;
  }, [connectedContent, projectId, isPwcUser]);

  const KFATitle = kfaNavigationData?.title || 'Key Focus Area';

  const KFATitleI18n = useKFATitleI18n(KFATitle);

  const contentTypeFnMap = useMemo(
    () => ({
      hypotheses: {
        title: KFATitle,
        viewContentPath: (hypothesisId) =>
          viewHypothesisPath(projectId, hypothesisId),
        setNarratives: setConnectedContent,
        narratives: get(connectedContent, 'hypotheses', []),
        noConnectedContentTitle: t('noKFAConnected', {
          KFATitle: KFATitleI18n,
        }),
      },
      analyses: {
        title: contentTypes.length === 1 ? t('analyses') : t('relatedAnalyses'),
        viewContentPath: analysesPath,
        setNarratives: setConnectedContent,
        narratives: get(connectedContent, 'analyses', []),
        noConnectedContentTitle:
          contentTypes.length === (kfaNavigationData?.isHidden ? 0 : 1)
            ? t('noAnalysesConnectedKfa', { KFATitle: KFATitleI18n })
            : t('noRelatedAnalysesConnectedToThisAnalysis'),
      },
    }),
    [
      setConnectedContent,
      analysesPath,
      projectId,
      connectedContent,
      contentTypes,
      kfaNavigationData,
      KFATitle,
      t,
      KFATitleI18n,
    ]
  );
  const connectionsElement = useMemo(() => {
    const connectionsRawElement = (
      <div className="connected-content-info-connections__description">
        {t('connections')}
      </div>
    );

    if (classModifier !== 'structured-analysis') {
      return (
        <FieldCommentWrapper fieldKey={CommentTarget.ConnectedContent}>
          {connectionsRawElement}
        </FieldCommentWrapper>
      );
    }

    return connectionsRawElement;
  }, [classModifier, t]);

  if (viewAsClient) {
    return null;
  }

  return (
    <div
      className={`connected-content ${classModifier}`}
      data-testid="connected-content"
    >
      <div className="connected-content-info-connections">
        <Icon
          name="hyperlink"
          width={16}
          className="icon fill connect-content-icon"
        />
        {connectionsElement}
      </div>
      <div className="linked-narratives" data-testid="linked-narratives">
        {contentTypes &&
          contentTypes.map((contentType) => {
            const analyses =
              contentTypes.length === 1
                ? CommentTarget.ConnectedAnalyses
                : CommentTarget.ConnectedRelatedAnalyses;
            return (
              <NarrativeSection
                key={`connected-content-${contentTypeFnMap[contentType].title}`}
                title={contentTypeFnMap[contentType].title}
                fieldKey={
                  contentType === 'hypotheses'
                    ? CommentTarget.ConnectedKfa
                    : analyses
                }
                isStructuredAnalysis={classModifier === 'structured-analysis'}
                narratives={contentTypeFnMap[contentType].narratives}
                getViewPath={contentTypeFnMap[contentType].viewContentPath}
                setNarratives={contentTypeFnMap[contentType].setNarratives}
                noConnectedContentTitle={
                  contentTypeFnMap[contentType].noConnectedContentTitle
                }
                error={error}
                sortByDateModified={sortByDateModified}
              />
            );
          })}
      </div>
    </div>
  );
};

export default ConnectedContent;
